'use strict';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isValidBic } from '../../utils/validators';
import { trackEvent } from '../../utils/gtm';
import { normalizeBic, maskBic, caretAfterMask } from './bic';
import { makeKneeScale } from './sliderScale';
import { Tick, ErrorIcon } from './Trust';

/**
 * Building blocks shared by the v2 application forms (yrityslaina, laskurahoitus).
 * Pure presentation + a few small hooks; all Redux wiring stays in the flow components.
 */

export const AUTH_BYPASS = process.env.REACT_APP_AUTH_BYPASS === 'true';
export const AUTH_CONTEXT_APPLICATION = 3;      // authenticationContext the live flows pass to createLoginToken
export const AUTH_CONTEXT_INVOICE_FUNDING = 4;  // InvoiceFundingApplication: Nets state "inv<id>" (laskurahoitus, same as the v1 form)
export const YTJ_TIMEOUT_MS = 6000;

export const fmt = (n) => Math.round(n).toLocaleString('fi-FI');
const fill = (value, { min, max }) => ({ '--pct': `${(((value - min) / (max - min)) * 100).toFixed(1)}%` });

export const validators = {
  bic: (v) => isValidBic(normalizeBic(v)),
  companyName: (v) => v.trim().length >= 2,
  phone: (v) => v.replace(/[\s-]/g, '').replace(/^\+/, '').length >= 7,
  email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()),
};
export const ERROR_TEXT = {
  bic: 'Tarkista Y-tunnus. Muoto on 1234567-8.',
  companyName: 'Kerro yrityksesi nimi.',
  phone: 'Tarkista puhelinnumero.',
  email: 'Tarkista sähköpostiosoite.',
  terms: 'Hyväksy ehdot jatkaaksesi.',
};

/** Shown when save failed and there is no application id to hand to Nets. */
export const SAVE_FAILED_PHONE_MSG = 'Hakemuksen tallennus epäonnistui. Yritä uudelleen tai soita 09 6829 8003.';

/**
 * Prefer the id returned by a successful save; fall back to the stage-1 savedId.
 * Never invent an id — callers must block the Nets redirect when this returns null.
 */
export function resolveSavedApplicationId(saved, savedId) {
  return (saved && saved.id) || savedId || null;
}

/**
 * Focus the invalid control. For terms errors, scroll so the submit button sits at the
 * viewport bottom (keeps switch + error + button visible together on ~390×664).
 */
export function focusAndScrollToFieldError(fieldKey, submitButtonId = 'kr-toStep2') {
  const el = document.getElementById(`kr-${fieldKey}`);
  if (!el) return;
  el.focus({ preventScroll: true });
  if (fieldKey === 'terms') {
    const btn = document.getElementById(submitButtonId);
    if (btn) {
      btn.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

const ALLOWED_FILE_TYPES = [
  'application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain',
];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

/* ---------------------------------------------------------------- icons */

export const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);
export const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
);
const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>
);

/* ---------------------------------------------------------------- inputs */

export function Range({ label, value, range, unit, big, onChange, ends, format = (v) => v }) {
  const stops = range.stops;
  const index = stops ? Math.max(0, stops.indexOf(value)) : 0;
  const last = stops ? stops.length - 1 : 0;
  return (
    <div className="kr-slider-field">
      <div className={`kr-slider-top${big ? ' kr-amount-row' : ''}`}>
        <span className="kr-s-label">{label}</span>
        <span className="kr-s-val">{format(value)} <small>{unit}</small></span>
      </div>
      {stops ? (
        <input
          type="range" className="kr-range" min={0} max={last} step={1} value={index}
          style={{ '--pct': `${((index / last) * 100).toFixed(1)}%` }}
          onChange={(e) => onChange(stops[parseInt(e.target.value, 10)])}
          aria-label={label} aria-valuemin={stops[0]} aria-valuemax={stops[last]} aria-valuenow={value} aria-valuetext={`${format(value)} ${unit}`}
        />
      ) : (
        <input
          type="range" className="kr-range" min={range.min} max={range.max} step={range.step || 1} value={value}
          style={fill(value, range)} onChange={(e) => onChange(parseInt(e.target.value, 10))}
          aria-label={label} aria-valuetext={`${format(value)} ${unit}`}
        />
      )}
      <div className="kr-range-ends"><span>{ends[0]}</span><span>{ends[1]}</span></div>
    </div>
  );
}

/**
 * Slider with a two-segment scale (see sliderScale.js): the native range drives an integer
 * position that maps to the value; arrow keys step by `range.step` so keyboard use stays
 * predictable even though the track is non-linear.
 */
export function KneeRange({ label, value, range, knee, unit, big, onChange, ends, format = (v) => v }) {
  const scale = makeKneeScale({ min: range.min, max: range.max, step: range.step || 1, knee: knee.value, kneeAt: knee.at });
  const pos = scale.toPosition(value);
  const onKeyDown = (e) => {
    const step = range.step || 1;
    const jump = { ArrowRight: step, ArrowUp: step, ArrowLeft: -step, ArrowDown: -step, PageUp: step * 10, PageDown: -step * 10 }[e.key];
    if (jump !== undefined) { e.preventDefault(); onChange(scale.snap(value + jump)); }
    else if (e.key === 'Home') { e.preventDefault(); onChange(range.min); }
    else if (e.key === 'End') { e.preventDefault(); onChange(range.max); }
  };
  return (
    <div className="kr-slider-field">
      <div className={`kr-slider-top${big ? ' kr-amount-row' : ''}`}>
        <span className="kr-s-label">{label}</span>
        <span className="kr-s-val">{format(value)} <small>{unit}</small></span>
      </div>
      <input
        type="range" className="kr-range" min={0} max={scale.positions} step={1} value={pos}
        style={{ '--pct': `${((pos / scale.positions) * 100).toFixed(1)}%` }}
        onChange={(e) => onChange(scale.toValue(parseInt(e.target.value, 10)))}
        onKeyDown={onKeyDown}
        aria-label={label} aria-valuemin={range.min} aria-valuemax={range.max} aria-valuenow={value} aria-valuetext={`${format(value)} ${unit}`}
      />
      <div className="kr-range-ends"><span>{ends[0]}</span><span>{ends[1]}</span></div>
    </div>
  );
}

export function Switch({ checked, onChange, label, children }) {
  return (
    <div className="kr-switch-field">
      <span className="kr-switch-q">{children}</span>
      <label className="kr-switch">
        <span className="kr-state">{checked ? 'Kyllä' : 'Ei'}</span>
        <input type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-label={label} />
        <span className="kr-track" aria-hidden="true" />
      </label>
    </div>
  );
}

// Consent block: text on top, one clear toggle row underneath.
// Error stays under the switch; the switch itself gets a red ring + aria when invalid.
export function Consent({ id, checked, onChange, error, rowLabel = 'Hyväksyn ehdot', ariaLabel = 'Käyttöehtojen hyväksyntä', children }) {
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={`kr-consent${error ? ' kr-has-error' : ''}`}>
      <p className="kr-consent-text">{children}</p>
      <label className="kr-consent-row">
        <span className="kr-consent-label">{rowLabel}</span>
        <span className={`kr-switch${error ? ' kr-switch-error' : ''}`}>
          <span className="kr-state">{checked ? 'Kyllä' : 'Ei'}</span>
          <input
            type="checkbox"
            role="switch"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            aria-label={ariaLabel}
            id={id}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={errorId}
          />
          <span className="kr-track" aria-hidden="true" />
        </span>
      </label>
      {error && <div className="kr-field-msg" id={errorId} role="alert"><ErrorIcon /><span>{error}</span></div>}
    </div>
  );
}

export function Field({ id, label, hint, hintTone, error, valid, required, optional, children }) {
  const cls = ['kr-field', error ? 'kr-has-error' : '', valid && !error ? 'kr-valid' : ''].join(' ').trim();
  return (
    <div className={cls}>
      <label htmlFor={id}>
        {label} {required && <span className="kr-req">*</span>}{optional && <span className="kr-optional">(valinnainen)</span>}
      </label>
      {children}
      {hint && !error && <div className={`kr-hint${hintTone ? ` kr-hint-${hintTone}` : ''}`}>{hint}</div>}
      {error && <div className="kr-field-msg" role="alert"><ErrorIcon /><span>{error}</span></div>}
    </div>
  );
}

export function LinkToggle({ open, onClick, controls, children }) {
  return (
    <button type="button" className={`kr-link-toggle${open ? ' kr-open' : ''}`} onClick={onClick} aria-expanded={open} aria-controls={controls}>
      {children}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
    </button>
  );
}

export function Assurance({ items }) {
  return (
    <ul className="kr-assurance">
      {items.map((t) => <li key={t}><span className="kr-tick"><Tick /></span> {t}</li>)}
    </ul>
  );
}

export function StepsBar({ step, labels = ['Tiedot', 'Tunnistautuminen'] }) {
  if (step === 'done') return null;
  return (
    <div className="kr-steps" aria-hidden="true">
      <div className={`kr-step-seg ${step === 1 ? 'kr-active' : 'kr-done'}`}><div className="kr-bar" /><div className="kr-lbl"><span className="kr-num">1</span> {labels[0]}</div></div>
      <div className={`kr-step-seg ${step === 2 ? 'kr-active' : ''}`}><div className="kr-bar" /><div className="kr-lbl"><span className="kr-num">2</span> {labels[1]}</div></div>
    </div>
  );
}

/* ---------------------------------------------------------------- step 2 blocks */

export function AttachmentsBlock({ open, onToggle, attachments, onAdd, onRemove, title = 'Väliaikatilinpäätös tai muu tukidokumentti' }) {
  return (
    <>
      <LinkToggle open={open} onClick={onToggle} controls="kr-attachments">
        Lisää liitteitä{attachments.length ? ` (${attachments.length})` : ' (valinnainen)'}
      </LinkToggle>
      {open && (
        <div id="kr-attachments" className="kr-uploader">
          <div className="kr-up-title">{title}</div>
          <div className="kr-up-sub">Nopeuttaa hakemuksesi käsittelyä. PDF, JPG tai PNG, enintään 10 MB.</div>
          <input id="kr-files" type="file" multiple hidden accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt" onChange={(e) => { onAdd(e.target.files); e.target.value = ''; }} />
          <button type="button" className="kr-btn kr-btn-ghost" onClick={() => document.getElementById('kr-files').click()}>
            <UploadIcon /> Valitse tiedostot
          </button>
          {attachments.length > 0 && (
            <ul className="kr-up-list">
              {attachments.map((a) => (
                <li key={a.id}>
                  <span>{a.fileName} · {(a.fileSize / 1024).toFixed(0)} kt</span>
                  <button type="button" onClick={() => onRemove(a.id)} aria-label={`Poista ${a.fileName}`}>Poista</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Step 2 authentication: one button that opens BankSelectionPanel, which lists every bank
 * and Mobiilivarmenne exactly as the live flows do. No separate Mobiilivarmenne shortcut —
 * the method is chosen in the panel.
 */
export function AuthButton({ label = 'Viimeistele hakemus tunnistautumalla', busy, onClick }) {
  return (
    <button type="button" className="kr-btn kr-btn-primary kr-auth-cta" onClick={onClick} disabled={busy}>
      {busy ? <span className="kr-spinner" /> : label}
    </button>
  );
}

export function StickyCta({ visible, label, onClick }) {
  return (
    <div className={`kr-sticky-cta${visible ? ' kr-show' : ''}`}>
      <button type="button" className="kr-btn kr-btn-primary" onClick={onClick}>{label} ↓</button>
      <div className="kr-mini"><Tick /> Maksuton & sitoumukseton · ~2 min</div>
    </div>
  );
}

export function SuccessIcon() {
  return (
    <div className="kr-success-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg></div>
  );
}

/* ---------------------------------------------------------------- hooks */

/** Fires kr_process_started once per process (instance + localStorage guard, like the live flows). */
export function useProcessStarted(processName) {
  const started = useRef(false);
  return useCallback(() => {
    const key = `kr_process_started_${processName}`;
    if (started.current || localStorage.getItem(key)) return;
    started.current = true;
    localStorage.setItem(key, 'true');
    trackEvent('kr_process_started', { process_name: processName });
  }, [processName]);
}

/** Mobile sticky CTA visibility: shown while the form card is scrolled out of view. */
export function useStickyVisible(cardRef, step) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!cardRef.current || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (window.innerWidth >= 760 || step === 'done') { setVisible(false); return; }
        setVisible(!(en.isIntersecting && en.intersectionRatio > 0.2));
      });
    }, { threshold: [0, 0.2, 1] });
    io.observe(cardRef.current);
    return () => io.disconnect();
  }, [cardRef, step]);
  return visible;
}

/** File picking with the same limits as the live flows' OptionalAttachments. */
export function useAttachments(onError) {
  const [attachments, setAttachments] = useState([]);
  const add = (fileList) => {
    const accepted = [];
    Array.from(fileList).forEach((file) => {
      if (file.size > MAX_FILE_BYTES) { onError(`Tiedosto ${file.name} on liian suuri. Maksimikoko on 10 MB.`); return; }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) { onError(`Tiedostotyyppi ei ole tuettu: ${file.name}. Tuetut: PDF, Word, Excel, JPG, PNG, GIF, TXT.`); return; }
      accepted.push({ id: Date.now() + Math.random(), file, description: '', fileName: file.name, fileSize: file.size });
    });
    if (accepted.length) setAttachments((a) => [...a, ...accepted]);
  };
  const remove = (id) => setAttachments((list) => list.filter((x) => x.id !== id));
  return { attachments, add, remove };
}

/**
 * Business id field behaviour: mask + caret, YTJ lookup and its status, company name prefill.
 * `fields`/`setFields` must contain `bic` and `companyName`.
 */
export function useBusinessId({ fields, setFields, setErrors, application, updateApplication, fetchYtj }) {
  const [ytjStatus, setYtjStatus] = useState('idle'); // idle | loading | ok | notfound
  const lastFetched = useRef('');
  const timer = useRef(null);
  const inputRef = useRef(null);
  const caret = useRef(null);

  // Restore the caret after the value was re-masked.
  useEffect(() => {
    const el = inputRef.current;
    if (caret.current !== null && el && document.activeElement === el) el.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  }, [fields.bic]);

  const bicNormalized = normalizeBic(fields.bic);
  useEffect(() => {
    if (!isValidBic(bicNormalized)) { setYtjStatus('idle'); return undefined; }
    if (bicNormalized === lastFetched.current) {
      if (application.isPrhFetched && application.companyName) setYtjStatus('ok'); // same id re-entered
      return undefined;
    }
    lastFetched.current = bicNormalized;
    updateApplication({ isPrhFetched: false, companyName: '' }); // clear a previous company before the new lookup
    setYtjStatus('loading');
    fetchYtj(bicNormalized);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setYtjStatus((s) => (s === 'loading' ? 'notfound' : s)), YTJ_TIMEOUT_MS);
    return () => clearTimeout(timer.current);
  }, [bicNormalized, fetchYtj, updateApplication]); // eslint-disable-line

  // Company name arrives from YTJ → prefill (user may still edit it).
  useEffect(() => {
    if (application.isPrhFetched && application.companyName) {
      setFields((f) => ({ ...f, companyName: application.companyName }));
      setErrors((e) => ({ ...e, companyName: undefined }));
      setYtjStatus('ok');
      clearTimeout(timer.current);
    }
  }, [application.isPrhFetched, application.companyName]); // eslint-disable-line

  const onChange = (e) => {
    const raw = e.target.value;
    const masked = maskBic(raw, fields.bic);
    caret.current = caretAfterMask(raw, e.target.selectionStart, masked);
    return masked;
  };
  const onBlurNormalize = () => setFields((f) => ({ ...f, bic: normalizeBic(f.bic) }));

  const hint = ytjStatus === 'loading' ? 'Haetaan yrityksen tietoja YTJ:stä…'
    : ytjStatus === 'ok' ? `Yrityksen tiedot haettu YTJ:stä${application.city ? ` · ${application.city}` : ''}`
      : ytjStatus === 'notfound' ? 'Yritystä ei löytynyt YTJ:stä. Tarkista Y-tunnus tai kirjoita yrityksen nimi itse.'
        : 'Haemme yrityksesi nimen automaattisesti Y-tunnuksella.';
  const hintTone = ytjStatus === 'ok' ? 'ok' : ytjStatus === 'notfound' ? 'warn' : undefined;

  return { inputRef, ytjStatus, bicNormalized, onChange, onBlurNormalize, hint, hintTone };
}

/** The company/contact fields both forms share; `extra` is rendered after the e-mail field. */
export function CompanyContactFields({ fields, setField, blurField, errors, isValid, bic, extra }) {
  return (
    <>
      <Field id="kr-bic" label="Y-tunnus" required error={errors.bic} valid={isValid('bic') && bic.ytjStatus === 'ok'} hint={bic.hint} hintTone={bic.hintTone}>
        <input id="kr-bic" ref={bic.inputRef} className="kr-control" inputMode="numeric" autoComplete="off" placeholder="1234567-8" maxLength={9}
          value={fields.bic} onChange={setField('bic')} onBlur={blurField('bic')} />
      </Field>
      <Field id="kr-companyName" label="Yrityksen nimi" required error={errors.companyName} valid={isValid('companyName') || bic.ytjStatus === 'ok'}>
        <input id="kr-companyName" className="kr-control" autoComplete="organization" placeholder="Esim. Yritys Oy"
          value={fields.companyName} onChange={setField('companyName')} onBlur={blurField('companyName')} />
      </Field>
      <Field id="kr-phone" label="Puhelinnumero" required error={errors.phone} valid={isValid('phone')}>
        <input id="kr-phone" className="kr-control" type="tel" inputMode="tel" autoComplete="tel" placeholder="040 123 4567"
          value={fields.phone} onChange={setField('phone')} onBlur={blurField('phone')} />
      </Field>
      <Field id="kr-email" label="Sähköposti" required error={errors.email} valid={isValid('email')} hint="Lähetämme tarjouksen ja sopimuksen sähköpostiin, tiedon myös tekstiviestillä.">
        <input id="kr-email" className="kr-control" type="email" inputMode="email" autoComplete="email" placeholder="nimi@yritys.fi"
          value={fields.email} onChange={setField('email')} onBlur={blurField('email')} />
      </Field>
      {extra}
    </>
  );
}

export function CampaignCode({ open, onToggle, value, onChange }) {
  return (
    <>
      <LinkToggle open={open} onClick={onToggle} controls="kr-campaign">Onko sinulla kampanjakoodi?</LinkToggle>
      {open && (
        <div id="kr-campaign">
          <Field id="kr-discountCode" label="Kampanjakoodi" optional>
            <input id="kr-discountCode" className="kr-control" autoComplete="off" placeholder="Syötä koodi" value={value} onChange={onChange} />
          </Field>
        </div>
      )}
    </>
  );
}
